import { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import { Button, ButtonGroup, Checkbox } from '@upmc/ims.component-library';

import Base from '../../../pages/Base/Base';
import styles from './BulkSelectModal.module.scss';

import WithLoadingHandler from '../../../hoc/withLoadingHandler/withLoadingHandler';

import MaterialIcon from '@material/react-material-icon';
import { nanoid } from 'nanoid';
const BulkSelectModal = ({
	options, //[]any
	onChange, //callback function
	onDone, //callback function
	selected, //[]any
	initialSelections, //constant copy of selected, []any
	buttonLabel, //string
	optionLabelKey, //string
	optionExtrasKey, //string
	uniqueIdentifierKey, //string
	handleSearchProp, //function(), nullable
	optionsPlaceholder, ///string, nullable
	optionsLoading = false, //bool, defaults to false
	searchDisabled,
}) => {
	const initiallySelectedOptions = initialSelections;
	const [modalOpen, setModalIsOpen] = useState(false);
	const [localOptions, setLocalOptions] = useState([...options]);
	const [localSelected, setLocalSelected] = useState([...selected]);
	const [searchTerm, setSearchTerm] = useState('');
	const [addSelections, setAddSelections] = useState([]);
	const [removeSelections, setRemoveSelections] = useState([]);
	const [uniqueIdentifier, setUniqueIdentifier] = useState('');

	useEffect(() => {
		//if a uniqueIdentifier key is provided, use this key as the key to identify the listed options, else use optionLabelKey
		if (uniqueIdentifierKey) {
			setUniqueIdentifier(uniqueIdentifierKey);
		} else {
			setUniqueIdentifier(optionLabelKey);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setLocalSelected([...selected]);
	}, [selected]);

	useEffect(() => {
		//remove selected options from options list
		let refreshedOptions = localOptions
			.filter(
				x =>
					localSelected.find(
						sel => sel[uniqueIdentifier] === x[uniqueIdentifier]
					) === undefined
			)
			.sort(function (a, b) {
				if (a[uniqueIdentifier] > b[uniqueIdentifier]) {
					return 1;
				} else {
					return -1;
				}
			});
		setLocalOptions(refreshedOptions);
		//reset search term
		setSearchTerm('');
		if (handleSearchProp) {
			handleSearchProp('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [localSelected]);

	useEffect(() => {
		setLocalOptions(options);
	}, [options]);

	const handleSearch = e => {
		e.preventDefault();

		if (searchTerm === '' || searchTerm === null) {
			setLocalOptions([...options]);
		} else {
			let filteredOptions = [...options].filter(option => {
				return option[uniqueIdentifier]
					.toLowerCase()
					.includes(searchTerm.toLowerCase());
			});

			let refreshedOptions = filteredOptions.filter(
				x =>
					localSelected.find(
						sel => sel[uniqueIdentifier] === x[uniqueIdentifier]
					) === undefined
			);

			setLocalOptions(refreshedOptions);
		}

		if (handleSearchProp) {
			handleSearchProp(searchTerm);
		}
	};

	const toggleModal = () => {
		setModalIsOpen(!modalOpen);
	};
	const handleClose = () => {
		setModalIsOpen(false);
	};
	const onDoneHandler = () => {
		onDone();
		handleClose();
	};

	const onCancel = () => {
		setLocalOptions([...options]);
		setLocalSelected([...selected]);
		onChange(initiallySelectedOptions);
		handleClose();
	};

	const removeHandler = () => {
		let removedOptions = [];
		onChange(
			localSelected
				.map(x => {
					if (
						removeSelections.find(
							sel => sel[uniqueIdentifier] === x[uniqueIdentifier]
						) === undefined
					) {
						return x;
					} else {
						removedOptions.push(x);
						return null;
					}
				})
				.filter(x => x !== null && x !== undefined)
		);
		setLocalOptions(
			localOptions
				.concat([...new Set(removedOptions)])
				.sort(function (a, b) {
					if (a[uniqueIdentifier] > b[uniqueIdentifier]) {
						return 1;
					} else {
						return -1;
					}
				})
		);
		setRemoveSelections([]);
	};

	const addHandler = () => {
		onChange(localSelected.concat(addSelections));
		setAddSelections([]);
	};

	const handleLocalSelect = option => {
		if (
			addSelections.find(
				x => x[uniqueIdentifier] === option[uniqueIdentifier]
			)
		) {
			let newAddSelections = addSelections.filter(
				x => x[uniqueIdentifier] !== option[uniqueIdentifier]
			);

			setAddSelections(newAddSelections);
		} else {
			let newAddSelections = [...addSelections];
			newAddSelections.push(option);
			setAddSelections(newAddSelections);
		}
	};

	const handleLocalSelectRemove = option => {
		if (
			removeSelections.find(
				x => x[uniqueIdentifier] === option[uniqueIdentifier]
			)
		) {
			let newRemoveSelections = removeSelections.filter(
				x => x[uniqueIdentifier] !== option[uniqueIdentifier]
			);
			setRemoveSelections(newRemoveSelections);
		} else {
			let newRemoveSelections = [...removeSelections];
			newRemoveSelections.push(option);
			setRemoveSelections(newRemoveSelections);
		}
	};

	const handleLocalRemoveAll = e => {
		if (e.target.checked) {
			setRemoveSelections(localSelected);
		} else setRemoveSelections([]);
	};

	const handleLocalSelectAll = e => {
		if (e.target.checked) {
			setAddSelections(localOptions);
		} else setAddSelections([]);
	};

	const determineChecked = (option, section) => {
		if (section === 'options') {
			return addSelections.find(
				x => x[uniqueIdentifier] === option[uniqueIdentifier]
			);
		} else if (section === 'selected') {
			return removeSelections.find(
				x => x[uniqueIdentifier] === option[uniqueIdentifier]
			);
		}
	};

	return (
		<>
			<Button
				variant='outlined'
				startIcon={<MaterialIcon icon='edit' />}
				onClick={toggleModal}
			>
				{buttonLabel}
			</Button>

			<Modal open={modalOpen} onClose={handleClose}>
				<div className={styles.Modal}>
					<Base title={buttonLabel}>
						<ButtonGroup className={styles.controls} center>
							<Button
								variant='outlined'
								startIcon={<MaterialIcon icon='cancel' />}
								onClick={() => onCancel()}
							>
								Cancel
							</Button>
							<Button
								variant='outlined'
								startIcon={<MaterialIcon icon='save' />}
								color='primary'
								onClick={onDoneHandler}
							>
								Done
							</Button>
						</ButtonGroup>
						<div className={styles.content}>
							<div className={styles.flex_row}>
								<div className={styles.selection_Section}>
									<p className={styles.section_Header}>
										Options
									</p>

									<div className={styles.section_content}>
										<WithLoadingHandler
											isLoading={optionsLoading}
										>
											{localOptions &&
											localOptions.length > 1 ? (
												<div
													className={styles.checkbox}
												>
													<Checkbox
														size='small'
														onChange={e =>
															handleLocalSelectAll(
																e
															)
														}
														label={'Select All'}
														value={'REMOVE ALL'}
													/>
												</div>
											) : null}
											{localOptions &&
											localOptions.length > 0 ? (
												localOptions.map(option => {
													return (
														<div
															className={
																styles.checkbox
															}
															key={nanoid()}
														>
															<Checkbox
																size='small'
																checked={determineChecked(
																	option,
																	'options'
																)}
																onChange={() =>
																	handleLocalSelect(
																		option
																	)
																}
																value={option}
																label={
																	option[
																		optionLabelKey
																	]
																}
															/>
															<>
																{
																	option[
																		optionExtrasKey
																	]
																}
															</>
														</div>
													);
												})
											) : (
												<p
													className={
														styles.options_placeHolder
													}
												>
													{optionsPlaceholder
														? optionsPlaceholder
														: 'No options available, try modifying search'}
												</p>
											)}
										</WithLoadingHandler>
									</div>
								</div>
								<div className={styles.selection_Controls}>
									<p>
										<strong>Search Options:</strong>
									</p>
									<form
										onSubmit={e => handleSearch(e)}
										className={styles.search}
									>
										<input
											type='text'
											className={styles.search}
											value={searchTerm}
											onChange={e => {
												setSearchTerm(e.target.value);
											}}
											disabled={searchDisabled}
										/>
									</form>
									<p>Press 'Enter' to search</p>
									<ButtonGroup vertical>
										<Button
											variant='outlined'
											startIcon={
												<MaterialIcon icon='west' />
											}
											color='primary'
											onClick={removeHandler}
										>
											remove
										</Button>
										<Button
											variant='outlined'
											startIcon={
												<MaterialIcon icon='east' />
											}
											color='primary'
											onClick={addHandler}
										>
											add
										</Button>
									</ButtonGroup>
								</div>
								<div className={styles.selection_Section}>
									<p className={styles.section_Header}>
										Selected
									</p>
									<div className={styles.section_content}>
										{localSelected &&
										localSelected.length ? (
											<div className={styles.checkbox}>
												<Checkbox
													size='small'
													onChange={e =>
														handleLocalRemoveAll(e)
													}
													value={'REMOVE ALL'}
													label={'Remove All'}
												/>
											</div>
										) : null}
										{localSelected.map(option => {
											return (
												<div
													className={styles.checkbox}
													key={nanoid()}
												>
													<Checkbox
														size='small'
														checked={determineChecked(
															option,
															'selected'
														)}
														onChange={() =>
															handleLocalSelectRemove(
																option
															)
														}
														value={option}
														label={
															option[
																optionLabelKey
															]
														}
													/>
													<>
														{
															option[
																optionExtrasKey
															]
														}
													</>
												</div>
											);
										})}
									</div>
								</div>
							</div>
						</div>
					</Base>
				</div>
			</Modal>
		</>
	);
};

export default BulkSelectModal;
